import { Button } from "components/atoms/Button";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import Sidebar from "components/molecules/Sidebar";
import { useCustomPlanUpdate } from "hooks/CustomPlan/useCustomPlanUpdate";
import { measureToMeasureInput } from "hooks/CustomPlan/useCustomPlanUpdate.utils";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import useCustomPlanSidebarState from "recoilStore/CustomPlan/useCustomPlanSidebarState";
import MeasureInput from "./GenericMeasureInputs";
import GenericMeasureInputs, { DEFAULT_MEASURE_INPUTS } from "./MeasureEffectInputs";
import MeasureOverrides from "./MeasureOverrides";

interface EditMeasureSidebarProps {
    closeSidebar: () => void;
    sidebarOpen: boolean;
    buildingModelUid: string;
    currentCarbonReductionPlanUid: string;
}

function EditMeasureSidebar({
    closeSidebar,
    sidebarOpen,
    buildingModelUid,
    currentCarbonReductionPlanUid,
}: EditMeasureSidebarProps) {
    const { state: customPlanSidebarState } = useCustomPlanSidebarState();
    const measure = customPlanSidebarState?.selectedRows[0];

    // [NIB 250821] Since this state is an object and can have different values modified by different components
    // it's important that any use of its setter function passes an updater function as its input to
    // the setter, not just a new version of the object. This allows React to queue all changes to the
    // state and will avoid the possibility of a race condition. See the docs for details:
    // https://react.dev/reference/react/useState#updating-state-based-on-the-previous-state
    const [measureInputs, setMeasureInputs] = useState<CarbonReductionMeasureInput>(DEFAULT_MEASURE_INPUTS);

    // Initializing reducer with selected row data
    useEffect(() => {
        if (!measure) {
            closeSidebar();
            return;
        }
        setMeasureInputs({
            ...measureToMeasureInput({ ...measure }),
        });
    }, [measure]);

    const { updateCustomPlanMeasures, isLoading } = useCustomPlanUpdate({
        buildingModelUid,
        buildingModelCarbonReductionPlanId: currentCarbonReductionPlanUid,
    });

    const onClickCancel = () => {
        closeSidebar();
    };

    const onClickApply = () => {
        posthog.capture("Edit measure");

        updateCustomPlanMeasures("edit", {
            ...measureInputs,
            selectedRows: customPlanSidebarState.selectedRows,
            measure: measureInputs,
            onSuccess: closeSidebar,
        });
    };

    if (isLoading) {
        return (
            <Sidebar onCancel={closeSidebar} sidebarOpen={sidebarOpen} title="Edit">
                <LoadingSpinner />
            </Sidebar>
        );
    }

    if (!measure) {
        return null;
    }

    return (
        <Sidebar onCancel={closeSidebar} sidebarOpen={sidebarOpen} title="Edit">
            <MeasureInput
                label="Implementation year"
                value={measureInputs.year}
                onChange={(value) => setMeasureInputs((m) => ({ ...m, year: value }))}
                placeholder="Edit measure year"
            />
            <MeasureOverrides measure={measure} measureInputs={measureInputs} setMeasure={setMeasureInputs} />
            <GenericMeasureInputs
                inputs={measureInputs}
                setInputs={setMeasureInputs}
                exclude={[
                    "measureCost",
                    "measureSize",
                    measure?.carbonReductionMeasureId !== "generic" ? "loadReductionEffects" : "",
                    measure?.carbonReductionMeasureId !== "generic" ? "consumptionReductionEffects" : "",
                    measure?.carbonReductionMeasureId !== "generic" ? "carbonIntensityReductionEffects" : "",
                    measure?.carbonReductionMeasureId !== "generic" ? "fuelCostReductionEffects" : "",
                ]}
                measure={measure}
            />

            {/* Footer */}
            <div className="filters-sidebar__footer">
                <Button type="primary" onClick={onClickApply} disabled={isLoading}>
                    {isLoading ? "Loading" : "Apply"}
                </Button>

                {!isLoading && (
                    <Button onClick={onClickCancel} type="link" disabled={isLoading}>
                        Cancel
                    </Button>
                )}
            </div>
        </Sidebar>
    );
}

export default EditMeasureSidebar;
